import {
	type Booking,
	type ReviewRatingStats,
	apiAppBookingReviewGetratingstats,
	apiAppBookingReviewMarkasread,
	apiAppBookingReviewMarkasunread,
	apiAppBookingReviewPostreply,
	apiAppBookingReviewPostunreply
} from '#lib/types/generated/index.js';
import { get, writable } from 'svelte/store';
import { adminStore } from './admin';
import { bookingStore } from './booking';

export const unreadReviewsCountStore = writable<number>(0);
export const reviewStatsStore = writable<ReviewRatingStats>({
	all: {
		averageRating: 0,
		reviewCount: 0
	},
	period: {
		averageRating: 0,
		reviewCount: 0
	},
	previousPeriod: {
		averageRating: 0,
		reviewCount: 0
	}
});

export function markReviewAsRead(booking: Booking) {
	if (get(adminStore)) {
		return;
	}

	if (!booking.surveyResult || booking.surveyResult.isRead) {
		return;
	}

	booking.surveyResult.isRead = true;
	const existing = get(bookingStore);
	const found = existing.find((b) => b.id === booking.id);
	if (found) {
		bookingStore.set(
			existing.map((r) =>
				r.id === booking.id
					? { ...booking, surveyResult: { ...booking.surveyResult!, isRead: true } }
					: r
			)
		);
	} else {
		bookingStore.set([
			...existing,
			{ ...booking, surveyResult: { ...booking.surveyResult, isRead: true } }
		]);
	}
	unreadReviewsCountStore.update((value) => (value ? value - 1 : 0));

	apiAppBookingReviewMarkasread({ id: booking.surveyResult.id }).catch(() => {
		if (!booking.surveyResult) {
			return;
		}

		booking.surveyResult.isRead = false;
		bookingStore.set(
			get(bookingStore).map((r) =>
				r.id === booking.id
					? { ...booking, surveyResult: { ...booking.surveyResult!, isRead: false } }
					: r
			)
		);
		unreadReviewsCountStore.update((value) => (value ? value + 1 : 1));
	});
}

export function markReviewAsUnread(booking: Booking) {
	if (!booking.surveyResult || !booking.surveyResult.isRead) {
		return;
	}

	booking.surveyResult.isRead = false;
	const existing = get(bookingStore);
	const found = existing.find((b) => b.id === booking.id);
	if (found) {
		bookingStore.set(
			existing.map((r) =>
				r.id === booking.id
					? { ...booking, surveyResult: { ...booking.surveyResult!, isRead: false } }
					: r
			)
		);
	} else {
		bookingStore.set([
			...existing,
			{ ...booking, surveyResult: { ...booking.surveyResult, isRead: false } }
		]);
	}
	unreadReviewsCountStore.update((value) => (value ? value + 1 : 1));

	apiAppBookingReviewMarkasunread({ id: booking.surveyResult.id }).catch(() => {
		if (!booking.surveyResult) {
			return;
		}

		booking.surveyResult.isRead = true;
		bookingStore.set(
			get(bookingStore).map((r) =>
				r.id === booking.id
					? { ...booking, surveyResult: { ...booking.surveyResult!, isRead: true } }
					: r
			)
		);
		unreadReviewsCountStore.update((value) => (value ? value - 1 : 0));
	});
}

export function markAllReviewsAsRead(callback?: () => void) {
	const surveyResultsSetAsRead: Booking[] = [];
	const currentUnread = get(unreadReviewsCountStore);

	bookingStore.set(
		get(bookingStore).map((r) => {
			if (!r.surveyResult || r.surveyResult.isRead) {
				return r;
			}

			surveyResultsSetAsRead.push(r);
			r.surveyResult.isRead = true;
			return r;
		})
	);

	unreadReviewsCountStore.update(() => 0);

	apiAppBookingReviewMarkasread({})
		.then(() => {
			callback?.();
		})
		.catch(() => {
			// Restore the previous state on error
			bookingStore.set(
				get(bookingStore).map((r) => {
					if (surveyResultsSetAsRead.includes(r) && r.surveyResult) {
						r.surveyResult.isRead = false;
					}
					return r;
				})
			);
			unreadReviewsCountStore.update(() => currentUnread);
		});
}

export async function fetchReviewStats(): Promise<ReviewRatingStats> {
	const res = await apiAppBookingReviewGetratingstats();
	reviewStatsStore.set(res);
	return res;
}

export async function replyToSurveyResult(booking: Booking, reply: string): Promise<Booking> {
	if (!booking.surveyResult) {
		throw new Error('No survey result for this booking');
	}

	const updated = await apiAppBookingReviewPostreply(booking.surveyResult.id, { reply });
	const updatedBooking: Booking = {
		...booking,
		...updated
	};
	bookingStore.update((list) =>
		list.map((b) => (b.id === updatedBooking.id ? updatedBooking : b))
	);
	return updatedBooking;
}

export async function cancelReplyToSurveyResult(booking: Booking): Promise<Booking> {
	if (!booking.surveyResult) {
		throw new Error('No survey result for this booking');
	}

	const updated = await apiAppBookingReviewPostunreply(booking.surveyResult.id);
	const updatedBooking: Booking = {
		...booking,
		...updated
	};
	bookingStore.update((list) =>
		list.map((b) => (b.id === updatedBooking.id ? updatedBooking : b))
	);
	return updatedBooking;
}
